// Daten der Eigentümer-Detailseite (Handoff Eigentümer §6.5). Erst Eigentümer und Zuordnungen
// (daraus Listenzeile, Rollen und Beträge wie in der Liste), dann die Bereiche parallel;
// ein fehlgeschlagener Bereich blendet nur sich selbst aus (fehler[bereich]).

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { heuteIso } from '../immobilien/format';
import { ladeAktivitaeten, ladeNotizen } from '../kontakte/api';
import type { MieterDokument } from '../mieter/mieterDokumente';
import { verguetungJe, type VerguetungRoh } from './useEigentuemerListe';
import type { FreigabeStatus, VerguetungEintrag } from './verwaltung';
import { istEtv } from './weg';
import type { Beteiligter } from './beteiligte';
import type { Aktivitaet, KontaktNotiz } from '../kontakte/types';
import { eigentuemerAnzeigename } from './anzeige';
import { istAktuell } from './einheitEigentum';
import { baueVerbund, type PortalVerbund, type VerbundEigentuemer, type VerbundProfil } from './portalVerbund';
import {
  baueEigZeilen, zuordnungsDaten, type EigentuemerRoh, type EigZeile, type EinheitEigRoh, type EinheitRoh, type MieterRoh, type ObjektEigRoh, type ObjektRoh,
} from './liste';

export type EigDetailRoh = EigentuemerRoh & Record<string, unknown>;

export interface TerminKurz { id: string; titel: string | null; start_datum: string; typ: string | null; objekt_id: string | null }
export type DokumentKurz = MieterDokument;
export interface Miteigentuemer { id: string; name: string; einheitId: string }
export interface FremdVerwalter { objektId: string; kontaktId: string; name: string; ansprechpartner: string; telefon: string; email: string }

export interface EigDetailDaten {
  eigentuemer: EigDetailRoh;
  zeile: EigZeile;
  objekte: ObjektRoh[];
  einheiten: EinheitRoh[];
  mieter: MieterRoh[];
  einheitEig: EinheitEigRoh[];
  /** profiles.status des Portal-Zugangs (über eigentuemer.profile_id, #584) */
  portalStatus: string | null;
  /** #584: weitere Datensätze am selben Zugang bzw. bestehende Zugänge zum Verknüpfen */
  portalVerbund: PortalVerbund | null;
  miteigentuemer: Miteigentuemer[];
  fremdverwalter: FremdVerwalter[];
  /** anstehende Termine (ab heute) */
  termine: TerminKurz[];
  dokumente: DokumentKurz[];
  notizen: KontaktNotiz[];
  aktivitaeten: Aktivitaet[];
  /** Vergütungshistorie (eigentuemer_verguetung), neueste zuerst */
  verguetungen: VerguetungEintrag[];
  freigaben: FreigabeZeile[];
  /** anstehende Eigentümerversammlungen der WEGs (Termine am Objekt) */
  etvTermine: TerminKurz[];
  /** Miteigentümer & Vertreter (eigentuemer_beteiligte) */
  beteiligte: Beteiligter[];
}

export interface FreigabeZeile {
  id: string; objekt_id: string | null; einheit_id: string | null; beschreibung: string; betrag: number | null;
  dienstleister_name: string | null; status: FreigabeStatus; angefragt_am: string; entschieden_am: string | null; notiz: string | null;
}

export type EigBereich = 'portalStatus' | 'portalVerbund' | 'miteigentuemer' | 'fremdverwalter' | 'termine' | 'dokumente' | 'notizen' | 'aktivitaeten' | 'freigaben' | 'etvTermine' | 'beteiligte';

export type EigDetailZustand =
  | { status: 'laedt' }
  | { status: 'fehlt' }
  | { status: 'fehler'; meldung: string }
  | { status: 'ok'; daten: EigDetailDaten; fehler: Partial<Record<EigBereich, string>> };

// Dokument-Spalten wie bei den Mietern (Öffnen über oeffneMieterDokument), ohne mieter_id
const EIG_DOK_SPALTEN = 'id, titel, datei_name, kategorie, datei_typ, datei_url, onedrive_url, gdrive_file_id, created_at';
const MIETER_FELDER = 'id, einheit_id, objekt_id, einheit, vorname, nachname, firma_name, status, kaltmiete, nebenkosten_vorauszahlung, heizkosten, weitere_kosten, miet_historie, mietbeginn, mietende';

function wert<T>(r: { data: T | null; error: { message: string } | null }): T {
  if (r.error) throw new Error(r.error.message);
  return (r.data ?? []) as T;
}

const maskiere = (s: string) => s.replace(/[\\%_]/g, '\\$&');

export async function ladeKern(id: string): Promise<Omit<EigDetailDaten, EigBereich> | null> {
  const e = await supabase.from('eigentuemer').select('*').eq('id', id).maybeSingle();
  if (e.error) throw new Error(e.error.message);
  if (!e.data) return null;
  const eigentuemer = e.data as EigDetailRoh;
  const [objektEig, eigeneEinheiten, verguetungRoh] = await Promise.all([
    supabase.from('objekt_eigentuemer').select('objekt_id, eigentuemer_id, vertrag_ab, vertrag_bis').eq('eigentuemer_id', id).then(wert<ObjektEigRoh[]>),
    supabase.from('einheit_eigentuemer').select('einheit_id, eigentuemer_id, mea, hausgeld_vorschuss, ruecklage_vorschuss, sev_status, vertrag_ab, vertrag_bis')
      .eq('eigentuemer_id', id).then(wert<EinheitEigRoh[]>),
    supabase.from('eigentuemer_verguetung').select('eigentuemer_id, betrag_je_einheit, gueltig_ab').eq('eigentuemer_id', id).then(wert<VerguetungRoh[]>),
  ]);
  const verguetungen = (verguetungJe(verguetungRoh).get(id) ?? []).sort((a, b) => b.ab.localeCompare(a.ab));
  const einheitIds = eigeneEinheiten.map(r => r.einheit_id);
  const ueberEinheiten = einheitIds.length
    ? wert(await supabase.from('objekt_einheiten').select('objekt_id').in('id', einheitIds)) as { objekt_id: string | null }[]
    : [];
  const objektIds = [...new Set([...objektEig.map(r => r.objekt_id), ...ueberEinheiten.map(r => r.objekt_id).filter((x): x is string => !!x)])];
  const [objekte, einheiten, mieter] = objektIds.length
    ? await Promise.all([
      supabase.from('objekte').select('*').in('id', objektIds).then(wert<ObjektRoh[]>),
      supabase.from('objekt_einheiten').select('id, objekt_id, bezeichnung, wohnflaeche').in('objekt_id', objektIds).then(wert<EinheitRoh[]>),
      supabase.from('mieter').select(MIETER_FELDER).in('objekt_id', objektIds).then(wert<MieterRoh[]>),
    ])
    : [[], [], []];
  const heute = heuteIso();
  const [zeile] = baueEigZeilen({
    eigentuemer: [eigentuemer], objektEig, einheitEig: eigeneEinheiten,
    daten: zuordnungsDaten(objekte, einheiten, mieter, heute), portal: new Map(), salden: null, heute, verguetungen: new Map([[id, verguetungen]]),
  });
  if (!zeile) return null;
  return { eigentuemer, zeile, objekte, einheiten, mieter, einheitEig: eigeneEinheiten, verguetungen };
}

async function ladeBereiche(k: Omit<EigDetailDaten, EigBereich>): Promise<{ daten: Pick<EigDetailDaten, EigBereich>; fehler: Partial<Record<EigBereich, string>> }> {
  const heute = heuteIso();
  const id = k.eigentuemer.id;
  const email = (k.eigentuemer.email ?? '').trim();
  const profilId = typeof k.eigentuemer.profile_id === 'string' ? k.eigentuemer.profile_id : null;
  const laufendeEinheiten = k.einheitEig.filter(r => istAktuell(r, heute)).map(r => r.einheit_id);
  const externeObjekte = [...new Set(k.zeile.zuordnungen.filter(z => z.rolle === 'SEV').map(z => z.objektId))];
  const wegObjekte = [...new Set(k.zeile.zuordnungen.filter(z => z.rolle === 'WEG' || z.rolle === 'WEG+SEV').map(z => z.objektId))];

  const lader: { [B in EigBereich]: () => Promise<EigDetailDaten[B]> } = {
    // #584: maßgeblich ist der verknüpfte Zugang (profile_id) – nur damit sieht das Portal den Datensatz
    portalStatus: async () => {
      if (!profilId) return null;
      const rows = wert(await supabase.from('profiles').select('status').eq('id', profilId)) as { status: string | null }[];
      return rows[0]?.status ?? null;
    },
    portalVerbund: async () => {
      const verknuepfte = wert(await supabase.from('eigentuemer').select('id, profile_id, anrede, vorname, nachname, firma_name')
        .not('profile_id', 'is', null).neq('id', id).limit(1000)) as VerbundEigentuemer[];
      const ids = [...new Set(verknuepfte.map(e => e.profile_id).filter((x): x is string => !!x))];
      const [perId, perMail] = await Promise.all([
        ids.length ? supabase.from('profiles').select('id, email, status, role').in('id', ids).then(wert<VerbundProfil[]>) : Promise.resolve([] as VerbundProfil[]),
        !profilId && email ? supabase.from('profiles').select('id, email, status, role').eq('role', 'eigentuemer').ilike('email', maskiere(email)).then(wert<VerbundProfil[]>)
          : Promise.resolve([] as VerbundProfil[]),
      ]);
      const profile = [...new Map([...perId, ...perMail].map(p => [p.id, p])).values()];
      return baueVerbund({ eigId: id, profilId, email, verknuepfte, profile });
    },
    // Miteigentum: weitere laufende Eigentümer derselben Einheiten (einheit_eigentuemer mit Historie)
    miteigentuemer: async () => {
      if (laufendeEinheiten.length === 0) return [];
      const rows = wert(await supabase.from('einheit_eigentuemer')
        .select('einheit_id, eigentuemer_id, vertrag_ab, vertrag_bis, eigentuemer:eigentuemer_id(anrede, vorname, nachname, firma_name)')
        .in('einheit_id', laufendeEinheiten).neq('eigentuemer_id', id)) as unknown as
        ({ einheit_id: string; eigentuemer_id: string; vertrag_ab: string | null; vertrag_bis: string | null; eigentuemer: EigentuemerRoh | null })[];
      return rows.filter(r => istAktuell(r, heute)).map(r => ({
        id: r.eigentuemer_id, einheitId: r.einheit_id, name: r.eigentuemer ? eigentuemerAnzeigename(r.eigentuemer) : 'Unbekannt',
      }));
    },
    // Fremdverwalter fremd verwalteter WEGs: objekt_kontakte mit Funktion „Immobilienverwaltung“
    fremdverwalter: async () => {
      if (externeObjekte.length === 0) return [];
      const rows = wert(await supabase.from('objekt_kontakte').select('objekt_id, kontakt_id, funktion, kontakt:kontakt_id(*)')
        .in('objekt_id', externeObjekte).ilike('funktion', '%verwaltung%')) as unknown as
        ({ objekt_id: string; kontakt_id: string; kontakt: Record<string, unknown> | null })[];
      const txt = (v: unknown) => (typeof v === 'string' ? v.trim() : '');
      return rows.map(r => {
        const kt = r.kontakt ?? {};
        const person = [txt(kt.vorname), txt(kt.nachname)].filter(Boolean).join(' ');
        const firma = txt(kt.firma_name);
        return {
          objektId: r.objekt_id, kontaktId: r.kontakt_id, name: firma || person || 'Fremdverwalter',
          ansprechpartner: firma ? person : '', telefon: txt(kt.festnetz) || txt(kt.handynummer), email: txt(kt.email),
        };
      });
    },
    termine: async () => wert(await supabase.from('termine').select('id, titel, start_datum, typ, objekt_id')
      .eq('eigentuemer_id', id).gte('start_datum', heute).order('start_datum', { ascending: true }).limit(50)) as TerminKurz[],
    dokumente: async () => wert(await supabase.from('dokumente').select(EIG_DOK_SPALTEN)
      .eq('eigentuemer_id', id).eq('is_deleted', false).order('created_at', { ascending: false }).limit(200)) as DokumentKurz[],
    notizen: async () => ladeNotizen('eigentuemer', id),
    aktivitaeten: async () => ladeAktivitaeten('eigentuemer', id),
    etvTermine: async () => {
      if (wegObjekte.length === 0) return [];
      const rows = wert(await supabase.from('termine').select('id, titel, start_datum, typ, objekt_id')
        .in('objekt_id', wegObjekte).gte('start_datum', heute).order('start_datum', { ascending: true }).limit(50)) as TerminKurz[];
      return rows.filter(istEtv);
    },
    freigaben: async () => (wert(await supabase.from('eigentuemer_freigaben')
      .select('id, objekt_id, einheit_id, beschreibung, betrag, dienstleister_name, status, angefragt_am, entschieden_am, notiz')
      .eq('eigentuemer_id', id).order('angefragt_am', { ascending: false }).limit(200)) as (FreigabeZeile & { betrag: number | string | null })[])
      .map(f => ({ ...f, betrag: f.betrag === null ? null : Number(f.betrag) })),
    beteiligte: async () => (wert(await supabase.from('eigentuemer_beteiligte')
      .select('id, typ, art, name, email, telefon, anteil, zustellvertreter, umfang, gueltig_bis, dokument_id')
      .eq('eigentuemer_id', id).order('created_at', { ascending: true })) as (Beteiligter & { anteil: number | string | null })[])
      .map(b => ({ ...b, anteil: b.anteil === null ? null : Number(b.anteil) })),
  };
  const schluessel = Object.keys(lader) as EigBereich[];
  const ergebnisse = await Promise.allSettled(schluessel.map(s => lader[s]()));
  const daten: Record<string, unknown> = { portalStatus: null, portalVerbund: null, miteigentuemer: [], fremdverwalter: [], termine: [], dokumente: [], notizen: [], aktivitaeten: [], freigaben: [], etvTermine: [], beteiligte: [] };
  const fehler: Partial<Record<EigBereich, string>> = {};
  ergebnisse.forEach((r, i) => {
    const s = schluessel[i]!;
    if (r.status === 'fulfilled') daten[s] = r.value;
    else fehler[s] = r.reason instanceof Error ? r.reason.message : 'Fehler';
  });
  return { daten: daten as Pick<EigDetailDaten, EigBereich>, fehler };
}

/** Gemeldet von Panels nach Änderungen, damit Kopf, Widgets und Seitenleiste neu laden. */
export const EIG_GEAENDERT = 'immo-eigentuemer-geaendert';
export function meldeEigentuemerGeaendert(eigentuemerId: string): void {
  window.dispatchEvent(new CustomEvent(EIG_GEAENDERT, { detail: eigentuemerId }));
}

export function useEigentuemerDetail(eigentuemerId: string): { zustand: EigDetailZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<EigDetailZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);

  useEffect(() => {
    let aktiv = true;
    (async () => {
      const kern = await ladeKern(eigentuemerId);
      if (!aktiv) return;
      if (!kern) { setZustand({ status: 'fehlt' }); return; }
      const { daten, fehler } = await ladeBereiche(kern);
      if (aktiv) setZustand({ status: 'ok', daten: { ...kern, ...daten }, fehler });
    })().catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [eigentuemerId, runde]);

  useEffect(() => {
    const hoeren = (e: Event) => { if ((e as CustomEvent<string>).detail === eigentuemerId) setRunde(x => x + 1); };
    window.addEventListener(EIG_GEAENDERT, hoeren);
    return () => window.removeEventListener(EIG_GEAENDERT, hoeren);
  }, [eigentuemerId]);

  const neuLaden = useCallback(() => setRunde(x => x + 1), []);
  return { zustand, neuLaden };
}
